import { Box, Divider, Typography } from '@/shared/ui';

import { parseVersionHistoryText } from '@/features/factsheet/lib/versionHistory';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';

import TextLinesDisplay from '../TextLinesDisplay';

interface ApproachTabProps {
  factsheet: FactsheetDetails;
}

interface TradeDataRowProps {
  label: string;
  value: boolean | string | undefined;
}

const tradeDataRowSx = {
  display: 'flex',
  gap: 1.5,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  pt: 0.5
};

const approachSectionSx = {
  display: 'flex',
  flexDirection: 'column'
};

const approachHeaderSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  py: 0.5
};

const headerTextSx = { fontWeight: 700 };
const dividerSx = { backgroundColor: 'gx.text', pr: 4 };
const rootSx = { display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 2.5 };
const textColSx = { display: 'flex', flexDirection: 'column' };

const TradeDataRow = ({ label, value }: TradeDataRowProps) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <Box sx={tradeDataRowSx}>
      <Typography sx={{ fontSize: isCompact ? 14 : undefined }}>{label}</Typography>
      <Typography sx={{ fontSize: isCompact ? 14 : undefined }}>{value ? 'Y' : 'N'}</Typography>
    </Box>
  );
};

const TradeDataApproachSection = ({ factsheet }: { factsheet: FactsheetDetails }) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <Box sx={{ ...approachSectionSx, flex: 1 }}>
      <Box sx={approachHeaderSx}>
        <Typography
          sx={{ ...headerTextSx, fontSize: isCompact ? 14 : undefined }}
          variant='body2'
        >
          Trade Data Approach
        </Typography>
        <Typography
          sx={{ ...headerTextSx, fontSize: isCompact ? 14 : undefined }}
          variant='body2'
        >
          Y/N
        </Typography>
      </Box>
      <Divider sx={dividerSx} />
      <TradeDataRow
        label='Priority to reported and concluded transactions'
        value={factsheet.priorityToTransactions}
      />
      <TradeDataRow
        label='Sole-sourced data from trading principals accepted'
        value={factsheet.soleSourcedDataAccepted}
      />
      <TradeDataRow
        label='Inter-affiliate data accepted as valid'
        value={factsheet.interAffiliateDataAccepted}
      />
    </Box>
  );
};

const ApproachTab = ({ factsheet }: ApproachTabProps) => {
  const isCompact = useIsFactsheetCompact();
  const approachLines = parseVersionHistoryText(factsheet, 'calculationApproach');

  return (
    <Box sx={{ ...rootSx, flexDirection: isCompact ? 'column' : 'row' }}>
      <TradeDataApproachSection factsheet={factsheet} />
      {approachLines && (
        <Box sx={{ ...textColSx, flex: 1 }}>
          <TextLinesDisplay
            lines={approachLines}
            typographyProps={isCompact ? { sx: { fontSize: 14 } } : undefined}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApproachTab;
